/**
 * useQuranPageCache — Prefetch & cache Quran page data
 *
 * Keeps an in-memory Map<pageNumber, Ayah[]> so page transitions
 * feel instant. Background-prefetches ±2 adjacent pages whenever
 * the current page changes.
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import type { Ayah, ReaderSettings } from '@/types/quran';
import { getAyahsByPage, getQuranEncTranslation } from '@/services/quran.service';

const TOTAL_PAGES = 604;
const PREFETCH_RADIUS = 2; // prefetch ±2 pages around current

interface FetchOptions {
  readMode: 'read' | 'tadabbur';
  settings: ReaderSettings;
}

/**
 * Build a cache key that incorporates settings which affect the API call,
 * so that when the user changes translation/mode, stale entries are ignored.
 */
function cacheKey(
  page: number,
  readMode: string,
  showTranslation: boolean,
  selectedTranslation?: number,
  tadabburSource?: string,
): string {
  return `${page}_${readMode}_${showTranslation}_${selectedTranslation ?? 0}_${tadabburSource ?? 'qf'}`;
}

export function useQuranPageCache(
  currentPage: number,
  options: FetchOptions,
) {
  const cacheRef = useRef<Map<string, Ayah[]>>(new Map());
  const inflightRef = useRef<Set<string>>(new Set());
  const [initialLoading, setInitialLoading] = useState(true);

  // Current page ayahs — reactive state so the component re-renders
  const [pageAyahs, setPageAyahs] = useState<Map<number, Ayah[]>>(new Map());

  const { readMode, settings } = options;

  const makeCacheKey = useCallback(
    (page: number) =>
      cacheKey(
        page,
        readMode,
        settings.showTranslation,
        settings.selectedTranslation,
        settings.tadabburTranslationSource,
      ),
    [readMode, settings.showTranslation, settings.selectedTranslation, settings.tadabburTranslationSource],
  );

  /**
   * Fetch a single page and store it in the cache.
   * Returns cached data if available.
   */
  const fetchPage = useCallback(
    async (pageNumber: number): Promise<Ayah[]> => {
      const key = makeCacheKey(pageNumber);

      // Already cached
      const cached = cacheRef.current.get(key);
      if (cached) return cached;

      // Already being fetched
      if (inflightRef.current.has(key)) {
        // Wait for it (simple poll)
        return new Promise<Ayah[]>((resolve) => {
          const interval = setInterval(() => {
            const data = cacheRef.current.get(key);
            if (data) {
              clearInterval(interval);
              resolve(data);
            }
          }, 50);
          // Timeout after 15s
          setTimeout(() => {
            clearInterval(interval);
            resolve([]);
          }, 15000);
        });
      }

      inflightRef.current.add(key);

      try {
        const needsTranslation = readMode === 'tadabbur' || settings.showTranslation;
        const translationIds = needsTranslation
          ? (settings.selectedTranslation ? [settings.selectedTranslation] : [161])
          : undefined;

        const pageAyahsData = await getAyahsByPage(pageNumber, {
          translations: translationIds,
        });

        // QuranEnc overlay for tadabbur mode
        const useQuranEnc =
          readMode === 'tadabbur' &&
          settings.tadabburTranslationSource === 'quranenc';

        if (useQuranEnc) {
          const surahNums = [
            ...new Set(pageAyahsData.map((a) => a.surahNumber).filter(Boolean)),
          ] as number[];
          const encDataMap = new Map<number, Map<number, string>>();
          await Promise.all(
            surahNums.map(async (sn) => {
              const encAyahs = await getQuranEncTranslation(sn);
              const ayaMap = new Map<number, string>();
              for (const ea of encAyahs) {
                ayaMap.set(parseInt(ea.aya, 10), ea.translation);
              }
              encDataMap.set(sn, ayaMap);
            }),
          );
          for (const ayah of pageAyahsData) {
            const sn = ayah.surahNumber ?? 0;
            const an = ayah.numberInSurah;
            const text = encDataMap.get(sn)?.get(an);
            if (text) {
              ayah.translations = [
                {
                  id: 0,
                  text,
                  resourceId: 0,
                  resourceName: 'QuranEnc thai_mokhtasar',
                  languageName: 'Thai',
                },
              ];
            }
          }
        }

        cacheRef.current.set(key, pageAyahsData);
        return pageAyahsData;
      } catch (error) {
        console.error(`Error prefetching page ${pageNumber}:`, error);
        return [];
      } finally {
        inflightRef.current.delete(key);
      }
    },
    [makeCacheKey, readMode, settings],
  );

  /**
   * Get cached ayahs for a page (synchronous).
   * Returns undefined if not yet cached.
   */
  const getCachedPage = useCallback(
    (pageNumber: number): Ayah[] | undefined => {
      const key = makeCacheKey(pageNumber);
      return cacheRef.current.get(key);
    },
    [makeCacheKey],
  );

  /**
   * Prefetch adjacent pages in the background.
   */
  const prefetchAround = useCallback(
    (page: number) => {
      for (let delta = -PREFETCH_RADIUS; delta <= PREFETCH_RADIUS; delta++) {
        const target = page + delta;
        if (target < 1 || target > TOTAL_PAGES) continue;
        const key = makeCacheKey(target);
        if (!cacheRef.current.has(key) && !inflightRef.current.has(key)) {
          // Fire and forget
          fetchPage(target);
        }
      }
    },
    [makeCacheKey, fetchPage],
  );

  /**
   * Flush cache (e.g. when settings change that affect translations).
   */
  const flushCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  // On mount / currentPage change — load current + prefetch
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const data = await fetchPage(currentPage);
      if (!cancelled) {
        setPageAyahs((prev) => {
          const next = new Map(prev);
          next.set(currentPage, data);
          return next;
        });
        setInitialLoading(false);

        // Also pre-populate adjacent pages into reactive state
        const adjacentPages = [currentPage - 1, currentPage + 1].filter(
          (p) => p >= 1 && p <= TOTAL_PAGES,
        );
        for (const adjPage of adjacentPages) {
          const cached = getCachedPage(adjPage);
          if (cached) {
            setPageAyahs((prev) => {
              const next = new Map(prev);
              next.set(adjPage, cached);
              return next;
            });
          }
        }
      }
      // Prefetch the surrounding pages
      prefetchAround(currentPage);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [currentPage, fetchPage, prefetchAround, getCachedPage]);

  // When settings / readMode change, flush and reload
  useEffect(() => {
    flushCache();
    // The currentPage effect will re-trigger because fetchPage changed
  }, [readMode, settings.showTranslation, settings.selectedTranslation, settings.tadabburTranslationSource]);

  /**
   * Get ayahs for a page. Returns from reactive state or cache.
   * If not available, returns empty array (will be fetched in background).
   */
  const getPageData = useCallback(
    (pageNumber: number): Ayah[] => {
      // First check reactive state
      const reactiveData = pageAyahs.get(pageNumber);
      if (reactiveData) return reactiveData;

      // Then check raw cache
      const cached = getCachedPage(pageNumber);
      if (cached) {
        // Populate reactive state for next render
        setPageAyahs((prev) => {
          const next = new Map(prev);
          next.set(pageNumber, cached);
          return next;
        });
        return cached;
      }

      // Trigger fetch in background
      fetchPage(pageNumber).then((data) => {
        if (data.length > 0) {
          setPageAyahs((prev) => {
            const next = new Map(prev);
            next.set(pageNumber, data);
            return next;
          });
        }
      });

      return [];
    },
    [pageAyahs, getCachedPage, fetchPage],
  );

  return {
    /** Ayahs for any page — returns instantly from cache, or [] while loading */
    getPageData,
    /** True only during the very first page load */
    initialLoading,
    /** Force re-fetch of all pages (e.g. after translation settings change) */
    flushCache,
    /** Pre-fetch pages around a specific page */
    prefetchAround,
  };
}
